import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { ApiProvider } from "../ApiProvider";
import { getInitialState } from "../helpers";
import { IMarkups } from "./getSharedSettings";
import { RootState } from "../store";
import { pricingActions } from "..";
import { handleError } from "../getThunkAndSlice";
import { EventInventoryRecord, getInventoryCacheEventId } from "@src/store/pricing/queryEventInventoryAllSlice";
import { IGlobalInventoryRawItem } from "./queryGlobalInventorySlice";

interface IUpdateSharingPayload {
	ListingIds: Array<number>;
	ShareOutSettings: Array<Omit<IMarkups, "Listing_ID">>;
	disableTooltip?: boolean;
}

const initialState = getInitialState([]);

export const updateSharing = createAsyncThunk<unknown, IUpdateSharingPayload>(
	"listingsSharing/updateSharing",
	async (payload, { rejectWithValue, getState, dispatch }) => {
		// Guard: never call the sharing endpoint for listings that are on hold (TAC-2491).
		// Defense-in-depth — the ShareUnshare UI already blocks this, but any caller is covered here.
		const stateBeforeRequest = getState() as RootState;
		const isItemOnHold = (item: EventInventoryRecord): boolean => {
			const globalItem = item as IGlobalInventoryRawItem;
			return !!(
				item.On_Hold ||
				item.Consignor_Hold ||
				(globalItem.Num_OnHold ?? 0) > 0 ||
				(globalItem.Num_Consignor_Hold ?? 0) > 0
			);
		};
		const requestedIds = new Set(payload.ListingIds);
		const heldListingIds = Array.from(
			new Set(
				[
					...stateBeforeRequest.pricing.eventInventoryAll.data,
					...(stateBeforeRequest.pricing.globalInventory.data as Array<EventInventoryRecord>),
				]
					.filter((item) => requestedIds.has(item.Listing_ID) && isItemOnHold(item))
					.map((item) => item.Listing_ID),
			),
		);
		if (heldListingIds.length) {
			if (!payload.disableTooltip) {
				toast.warning(`Cannot update sharing for listings on hold: ${heldListingIds.join(", ")}`);
			}
			return rejectWithValue("Sharing is unavailable for listings on hold");
		}

		try {
			await ApiProvider.default.post("/api/pricing/UpdateListingsSharing", payload);

			const rootState = getState() as RootState;
			const eventId = getInventoryCacheEventId(rootState);
			const inventoryAllState = rootState.pricing.eventInventoryAll.data;

			const updatedListingIds = new Set(payload.ListingIds);
			const newSplits = payload.ShareOutSettings?.[0]?.AllowedSplits ?? 0;
			const newExportDestinationCsv = payload.ShareOutSettings.map((s) => s.Export_Destination_ID).join(",");

			const newData = inventoryAllState.map((x) =>
				updatedListingIds.has(x.Listing_ID)
					? { ...x, StubHub_Splits: newSplits, Export_Destination_ID_CSV: newExportDestinationCsv }
					: x,
			);

			dispatch(pricingActions.updateInventoryAll({ data: newData, eventId }));

			if (rootState.pricing.globalInventory.data.length) {
				const newGlobalData = (rootState.pricing.globalInventory.data as Array<EventInventoryRecord>).map((x) =>
					updatedListingIds.has(x.Listing_ID)
						? { ...x, StubHub_Splits: newSplits, Export_Destination_ID_CSV: newExportDestinationCsv }
						: x,
				);
				dispatch(pricingActions.updateGlobalInventory({ data: newGlobalData }));
			}

			if (payload.disableTooltip) return;

			const allListingIDs = payload.ListingIds.reduce((acc, item) => acc + ` ${item}`, "");
			toast.success(`Successfully updated listings: ${allListingIDs}`);
		} catch (error) {
			const message = handleError(error);
			toast.warning(message);
			return rejectWithValue(message);
		}
	},
);

export const listingsSharingSlice = createSlice({
	name: "listingsSharing",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updateSharing.pending, (state) => {
				state.loading = true;
			})
			.addCase(updateSharing.fulfilled, (state, _action) => {
				state.loading = false;
				state.version++;
				state.init = true;
				state.error = null;
			})
			.addCase(updateSharing.rejected, (state, action) => {
				state.loading = false;
				state.error = `${action.payload}`;
			});
	},
});
